import { useState } from 'react';

type Badge = { label: string; tone: 'xpec' | 'nk' };

const xpec: Badge = { label: 'Xpec', tone: 'xpec' };
const nk: Badge = { label: 'NK PR', tone: 'nk' };

const government = [
  { org: 'Assam State Rural Livelihoods Mission (ASRLM), Dept. of Panchayat & Rural Development, Govt. of Assam', badge: xpec },
  { org: 'Commissionerate of Panchayat & Rural Development, Govt. of Assam', badge: xpec },
  { org: 'Directorate of Food, Civil Supplies & Consumer Affairs, Govt. of Assam', badge: xpec },
  { org: 'Jal Jeevan Mission, Assam — Public Health Engineering Dept., Govt. of Assam', badge: xpec },
  { org: 'Department of Tourism, Government of Nagaland', badge: xpec },
];

const psus = [
  { org: 'AMTRON — Assam Electronics Development Corporation Ltd. (AEDC)', badge: xpec },
  { org: 'Assam Petro-Chemicals Limited (APL)', badge: xpec },
  { org: 'Brahmaputra Valley Fertilizer Corporation Ltd. (BVFCL), Namrup', badge: xpec },
];

const politicians = [
  {
    group: 'Ministers',
    rows: [
      { name: 'Vijay Sharma', role: 'Deputy Chief Minister, Chhattisgarh', badge: nk },
      { name: 'Temjen Imna Along', role: 'Minister of Tourism, Nagaland', badge: xpec },
      { name: 'Ajanta Neog', role: 'Cabinet Minister, Assam', badge: xpec },
      { name: 'Ranjeet Kumar Das', role: 'Cabinet Minister, Assam', badge: xpec },
      { name: 'Sushanta Borgohain', role: 'Cabinet Minister, Assam', badge: xpec },
      { name: 'Rupesh Gowala', role: 'Cabinet Minister, Assam', badge: xpec },
      { name: 'Prashanta Phukan', role: 'Cabinet Minister, Assam', badge: xpec },
    ],
  },
  {
    group: 'Members of Parliament',
    rows: [
      { name: 'Vishnu Dayal Ram', role: 'MP, Jharkhand', badge: nk },
      { name: 'Bidyut Baran Mahato', role: 'MP, Jharkhand', badge: nk },
      { name: 'Kalicharan Singh', role: 'MP, Jharkhand', badge: nk },
      { name: 'Aditya Sahu', role: 'MP, Jharkhand', badge: nk },
      { name: 'Ganesh Singh', role: 'MP, Madhya Pradesh', badge: nk },
      { name: 'Janardan Singh Sigriwal', role: 'MP, Bihar', badge: nk },
      { name: 'Jyotirmay Singh Mahato', role: 'MP, West Bengal', badge: nk },
    ],
  },
  {
    group: 'MLAs',
    rows: [
      { name: 'Prakash Das', role: 'MLA, Assam', badge: xpec },
      { name: 'Sashi Kanta Das', role: 'MLA, Assam', badge: xpec },
      { name: 'Suruj Dehingia', role: 'MLA, Assam', badge: xpec },
      { name: 'Bhaskar Sharma', role: 'MLA, Assam', badge: xpec },
      { name: 'Bolin Chetia', role: 'MLA, Assam', badge: xpec },
      { name: 'Bhupen Ray', role: 'MLA, Assam', badge: xpec },
      { name: 'Naba Kanta Doley', role: 'MLA, Assam', badge: xpec },
      { name: 'Tovihoto Ayemi', role: 'MLA, Nagaland', badge: xpec },
      { name: 'Kedar Hazra', role: 'MLA', badge: nk },
      { name: 'Rituraj Kumar', role: 'MLA', badge: nk },
      { name: 'Ruhel Ranjan', role: 'MLA', badge: nk },
      { name: 'Dr. Manjrik Mrinal', role: 'MLA', badge: nk },
      { name: 'Chetan Anand', role: 'MLA', badge: nk },
      { name: 'Siddharth Saurabh', role: 'MLA', badge: nk },
    ],
  },
  {
    group: 'Advisors & Senior Leaders',
    rows: [
      { name: 'Khehovi Yepthomi', role: 'Advisor, Government of Nagaland', badge: xpec },
      { name: 'Dr. Ravindra Kumar Ray', role: 'Former State President, BJP Jharkhand; former Minister', badge: nk },
      { name: 'Dr. Arun Kumar', role: 'Former MP; founder member, JDU', badge: nk },
      { name: 'Deepak Prakash', role: 'Former MP (Rajya Sabha)', badge: nk },
    ],
  },
  {
    group: 'Political Organisations',
    rows: [
      { name: 'Bharatiya Janata Party — Assam Pradesh', role: 'State unit', badge: xpec },
      { name: 'Bharatiya Janata Party — Nagaland', role: 'State unit', badge: xpec },
      { name: 'Bharatiya Janata Party', role: 'Ground research engagement', badge: nk },
      { name: 'Janata Dal (United)', role: 'National party', badge: nk },
    ],
  },
];

const categories = [
  { key: 'government', label: 'Government', count: government.length },
  { key: 'psu', label: 'PSUs & Undertakings', count: psus.length },
  { key: 'political', label: 'Politicians & Organisations', count: politicians.reduce((n, g) => n + g.rows.length, 0) },
];

function Badge({ badge }: { badge: Badge }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-mono uppercase tracking-wider whitespace-nowrap ${
        badge.tone === 'xpec'
          ? 'bg-primary-100 text-primary-800'
          : 'bg-accent-100 text-accent-900'
      }`}
    >
      {badge.label}
    </span>
  );
}

function Row({ name, role, badge, n }: { name: string; role: string; badge: Badge; n: number }) {
  return (
    <div className="group flex items-center gap-4 px-5 md:px-6 py-4 border-t border-background-200 first:border-t-0 hover:bg-background-100 transition-colors">
      <span className="w-8 text-[12px] font-mono text-foreground-400 shrink-0">0{n}</span>
      <div className="flex-1 min-w-0">
        <p className="text-[15px] text-foreground-950 font-medium">{name}</p>
        <p className="text-[12px] text-foreground-500 mt-0.5 leading-relaxed">{role}</p>
      </div>
      <div className="shrink-0">
        <Badge badge={badge} />
      </div>
    </div>
  );
}

export default function OurWorks() {
  const [active, setActive] = useState('government');

  return (
    <section id="our-works" className="relative w-full bg-background-50 py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 bg-grid-fine opacity-50 mask-radial-fade pointer-events-none"></div>

      <div className="relative px-6 md:px-16 lg:px-28">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[10px] tracking-[0.3em] uppercase text-foreground-500 font-mono">10 / Our Works</span>
            </div>
            <h2 data-reveal className="text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.08] text-foreground-950 tracking-tight font-semibold">
              A roster built on <span className="text-primary-700">trust</span>.
            </h2>
            <p data-reveal="fade" style={{ ['--reveal-delay' as never]: '120ms' }} className="mt-5 max-w-xl text-[15px] md:text-base leading-[1.8] text-foreground-600">
              From government departments and public sector undertakings to elected
              leaders and political organisations across India — the institutions
              that shape public life are the ones we serve.
            </p>
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((c) => {
            const isActive = active === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setActive(c.key)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-mono uppercase tracking-wider transition cursor-pointer whitespace-nowrap border ${
                  isActive
                    ? 'bg-primary-500 text-background-50 border-primary-500'
                    : 'bg-background-50 text-foreground-600 border-background-200 hover:border-primary-400'
                }`}
              >
                {c.label}
                <span
                  className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-mono ${
                    isActive ? 'bg-background-50/20' : 'bg-secondary-100 text-secondary-900'
                  }`}
                >
                  {c.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Government / PSU list */}
        {(active === 'government' || active === 'psu') && (
          <div className="rounded-2xl border border-background-200 bg-background-50 overflow-hidden">
            {active === 'government' &&
              government.map((g, i) => (
                <Row key={g.org} name={g.org} role="Client organisation" badge={g.badge} n={i + 1} />
              ))}
            {active === 'psu' &&
              psus.map((p, i) => (
                <Row key={p.org} name={p.org} role="Public sector undertaking" badge={p.badge} n={i + 1} />
              ))}
          </div>
        )}

        {/* Politicians — grouped by sub-category */}
        {active === 'political' && (
          <div className="flex flex-col gap-6">
            {politicians.map((g) => (
              <div key={g.group} className="rounded-2xl border border-background-200 bg-background-50 overflow-hidden">
                <div className="flex items-center justify-between px-5 md:px-6 py-4 bg-background-100/70">
                  <p className="text-[11px] tracking-[0.25em] uppercase text-foreground-500 font-mono">{g.group}</p>
                  <span className="text-[11px] font-mono text-foreground-400">{g.rows.length} served</span>
                </div>
                <div>
                  {g.rows.map((r, i) => (
                    <Row key={r.name} name={r.name} role={r.role} badge={r.badge} n={i + 1} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footnote */}
        <p data-reveal="fade" className="mt-8 text-[12px] leading-relaxed text-foreground-400">
          Representation reflects engagements delivered by Prabhaw through its partners — XPEC Innovations and NKPR Promotion. Roles shown are as of the time of engagement.
        </p>
      </div>
    </section>
  );
}